import React, { useState } from 'react';
import {
  Stack,
  TextField,
  Text,
  IStackTokens,
  IStackStyles,
  PrimaryButton,
  DefaultButton,
  Dialog,
  DialogType,
  DialogFooter,
  IChoiceGroupOption,
  ChoiceGroup,
} from '@fluentui/react';
import { Student, SubjectStudents, Professor } from 'src/models';
import { createPdf } from 'src/endpoints/sendPdf';
import { getTitleAndNameByDepartment } from 'src/utils/academiaProfesores';
import { Button } from '@fluentui/react-components';

const stackTokensVertical: IStackTokens = {
  childrenGap: 20,
};

const stackTokensHorizontal: IStackTokens = {
  childrenGap: 20,
};

const stackStyles: IStackStyles = {
  root: {
    height: '100%',
    overflow: 'auto',
    width: '100%',
    maxWidth: 600,
    margin: '0 auto',
    padding: 20,
    boxShadow: '0 0 10px rgba(0, 0, 0)',
    borderRadius: 5,
    backgroundColor: 'white',
  },
};

const outerStackStyles: IStackStyles = {
  root: {
    height: '90vh',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    boxSizing: 'border-box',
  },
};

const options: IChoiceGroupOption[] = [
  { key: 'Ordinario', text: 'Ordinario' },
  { key: 'Extraordinario', text: 'Extraordinario' },
];

type Props = {
  alumnos: Student[];
  materiaAlumno: SubjectStudents;
  plan: string;
  profesor: Professor;
  academia: string;
  onBack: () => void;
};

const ChangeRequest: React.FC<Props> = ({
  alumnos,
  materiaAlumno,
  plan,
  profesor,
  academia,
  onBack,
}) => {
  const [motivo, setMotivo] = useState<string>('');
  const [calificacionesIncorrectas, setCalificacionesIncorrectas] = useState<
    Record<string, string>
  >({});
  const [calificacionesCorrectas, setCalificacionesCorrectas] = useState<
    Record<string, string>
  >({});
  const [errorCalificacionCorrecta, setErrorCalificacionCorrecta] = useState<
    Record<string, string | undefined>
  >({});
  const [isDialogVisible, setIsDialogVisible] = useState<boolean>(false);
  const [isConfirmDisabled, setIsConfirmDisabled] = useState(false);
  const [tipoOportunidad, setTipoOportunidad] = useState<string>('Ordinario');

  const validateCalificacion = (calificacion: string) => {
    if (!calificacion) {
      return 'Calificación es requerida.';
    }

    if (
      materiaAlumno.SubjectName === 'Servicio social' ||
      materiaAlumno.SubjectName === 'Estancia de investigación'
    ) {
      return calificacion !== 'CU' && calificacion !== 'NC'
        ? 'Calificación debe ser "CU" o "NC".'
        : undefined;
    } else {
      if (isNaN(parseInt(calificacion)) && calificacion !== 'NP') {
        return 'Calificación debe ser NP o un número.';
      }

      if (parseInt(calificacion) < 0 || parseInt(calificacion) > 100) {
        return 'Calificación debe estar entre 0 y 100.';
      }
    }

    return undefined;
  };

  const handleConfirm = async () => {
    if (isConfirmDisabled) return;
    setIsConfirmDisabled(true);

    try {
      const response = await createPdf(
        alumnos,
        materiaAlumno,
        plan,
        profesor,
        tipoOportunidad,
        calificacionesIncorrectas,
        calificacionesCorrectas,
        motivo,
        academia,
        getTitleAndNameByDepartment(academia)!
      );
      // Confirm that the sent PDF data is being received
      if (response.status === 202) {
        alert('Solicitud enviada satisfactoriamente!');
      } else {
        alert('Error al enviar solicitud.');
      }
      /*
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      saveAs(pdfBlob, 'solicitud_cambio.pdf');
      */
      setIsDialogVisible(false);
    } catch (error: any) {
      throw new Error('Error generating PDF: ' + error.message);
    }
  };

  return (
    <Stack styles={outerStackStyles}>
      <Stack styles={stackStyles} tokens={stackTokensVertical}>
        <Stack horizontal horizontalAlign="start">
          <Button
            appearance="secondary"
            size="large"
            style={{ backgroundColor: '#f5f5f5', border: '1px solid #ccc' }}
            onClick={onBack}
          >
            Volver
          </Button>
        </Stack>
        <Text
          style={{ fontWeight: 'bold', textAlign: 'center' }}
          variant="xLarge"
        >
          Solicitud de cambio
        </Text>
        <Stack tokens={stackTokensVertical}>
          <Stack horizontal tokens={stackTokensHorizontal}>
            <Text style={{ fontWeight: 'bold' }}>Materia:</Text>
            <Text>
              {materiaAlumno.SubjectName} (Clave: {materiaAlumno.SubjectCode},
              Grupo: {materiaAlumno.Group})
            </Text>
          </Stack>
          <Stack horizontal tokens={stackTokensHorizontal}>
            <Text style={{ fontWeight: 'bold' }}>Profesor:</Text>
            <Text>
              {profesor.TeacherName} ({profesor.EmployeeId})
            </Text>
          </Stack>
          <Stack horizontal tokens={stackTokensHorizontal}>
            <Text style={{ fontWeight: 'bold' }}>Coordinador Academia:</Text>
            <Text>{getTitleAndNameByDepartment(academia)}</Text>
          </Stack>
          <Stack horizontal tokens={stackTokensHorizontal}>
            <Text style={{ fontWeight: 'bold' }}>Oportunidad:</Text>
            <ChoiceGroup
              defaultSelectedKey="Ordinario"
              options={options}
              onChange={(_, option) => {
                setTipoOportunidad(option?.key || 'Ordinario');
              }}
              label=""
              required={true}
            />
          </Stack>
        </Stack>
        {alumnos.map((alumno) => (
          <Stack key={alumno.StudentId} tokens={stackTokensVertical}>
            <Stack horizontal tokens={stackTokensHorizontal}>
              <Text style={{ fontWeight: 'bold' }}>Alumno:</Text>
              <Text>
                {alumno.Name} ({alumno.StudentId})
              </Text>
            </Stack>
            <Stack horizontal tokens={stackTokensHorizontal}>
              <TextField
                label="Calificación Incorrecta"
                onChange={(_, newValue) => {
                  setCalificacionesIncorrectas((prev) => ({
                    ...prev,
                    [alumno.StudentId]: newValue || '',
                  }));
                }}
                value={calificacionesIncorrectas[alumno.StudentId] || ''}
              />
              <TextField
                label="Calificación Correcta"
                onChange={(_, newValue) => {
                  const error = validateCalificacion(newValue || '');
                  setErrorCalificacionCorrecta((prev) => ({
                    ...prev,
                    [alumno.StudentId]: error,
                  }));
                  setCalificacionesCorrectas((prev) => ({
                    ...prev,
                    [alumno.StudentId]: newValue || '',
                  }));
                }}
                errorMessage={errorCalificacionCorrecta[alumno.StudentId]}
                value={calificacionesCorrectas[alumno.StudentId] || ''}
              />
            </Stack>
          </Stack>
        ))}
        <TextField
          label="Motivo"
          multiline
          rows={4}
          onChange={(_, newValue) => {
            setMotivo(newValue || '');
          }}
        />
        <PrimaryButton
          onClick={() => {
            if (
              !calificacionesCorrectas ||
              !calificacionesIncorrectas ||
              !motivo ||
              Object.values(errorCalificacionCorrecta).some((error) => error)
            ) {
              return;
            }
            setIsDialogVisible(true);
          }}
        >
          Enviar solicitud
        </PrimaryButton>
      </Stack>
      <Dialog
        hidden={!isDialogVisible}
        onDismiss={() => setIsDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: '¿Está seguro de solicitar estos cambios?',
          closeButtonAriaLabel: 'Cerrar',
          styles: {
            content: {
              width: '100%',
              height: '100%',
            },
          },
        }}
        modalProps={{
          isBlocking: true,
        }}
      >
        <Stack tokens={stackTokensVertical}>
          {alumnos.map((alumno) => (
            <Stack key={alumno.StudentId} tokens={stackTokensVertical}>
              <Text>
                Alumno: {alumno.Name} ({alumno.StudentId})
              </Text>
              <Stack horizontal tokens={stackTokensHorizontal}>
                <Text>
                  Calificación Incorrecta:{' '}
                  {calificacionesIncorrectas[alumno.StudentId]}
                </Text>
                <Text>
                  Calificación Correcta:{' '}
                  {calificacionesCorrectas[alumno.StudentId]}
                </Text>
              </Stack>
            </Stack>
          ))}
          <Text style={{ wordBreak: 'break-word' }}>Motivo: {motivo}</Text>
          <Text style={{ fontWeight: 'bold' }}>
            Oportunidad: {tipoOportunidad}
          </Text>
        </Stack>
        <DialogFooter>
          <DefaultButton
            onClick={() => setIsDialogVisible(false)}
            text="Cancelar"
          />
          <PrimaryButton
            onClick={handleConfirm}
            text="Confirmar"
            disabled={isConfirmDisabled}
          />
        </DialogFooter>
      </Dialog>
    </Stack>
  );
};

export default ChangeRequest;
